import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { some } from 'lodash-es';
import * as immutable from 'object-path-immutable';
import { EMPTY, Observable, of, switchMap, tap } from 'rxjs';
import { RESOURCE_STATE_TOKEN } from '~backoffice/app/~shared/constants/global.constants';
import { PageName } from '~backoffice/app/~shared/models/shared.models';
import { ResourceStateModel } from '~backoffice/app/~shared/models/store.models';
import { OrganizationService } from '~backoffice/app/~shared/services/organization.service';
import { ProductService } from '~backoffice/app/~shared/services/product.service';
import { TaxService } from '~backoffice/app/~shared/services/tax.service';
import { UserService } from '~backoffice/app/~shared/services/user.service';
import { removeNilProperties } from '~backoffice/app/~shared/utils/utils';
import {
  ApplyFilters,
  GetData,
  LoadMore,
  NavigateCursor,
  PatchItem,
  RemoveItem,
  ResetFilters,
  ResetState,
  SetPagination,
  ToggleDisposition,
  ToggleItemSelection,
  ToggleItemsSelection
} from '~backoffice/app/~store/actions/resource.actions';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { Pagination } from '~core/models/core.models';
import {
  CustomInvoiceStatus,
  InvoiceStatus,
  InvoiceType
} from '~core/models/invoice.models';
import { ReferenceDataType } from '~core/models/organization.models';
import { FreeRequestParams, Resource } from '~core/models/shared.models';
import { AuditService } from '~core/services/audit.service';
import { CurrenciesService } from '~core/services/currencies.service';
import { DashboardService } from '~core/services/dashboard.service';
import { EstablishmentService } from '~core/services/establishment.service';
import { ImportTaskService } from '~core/services/import-task.service';
import { InvoiceService } from '~core/services/invoice.service';
import { OrganizationUsersService } from '~core/services/organization-users.service';
import { PaymentService } from '~core/services/payment.service';
import { ReceiptService } from '~core/services/receipt.service';
import { ReferenceDataService } from '~core/services/reference-data.service';
import { RolesService } from '~core/services/roles.service';
import { SalesPointService } from '~core/services/sales-point.service';
import { StickerMovementService } from '~core/services/sticker-movement.service';
import { TaxRulesService } from '~core/services/tax-rules.service';
import { UnitMeasureService } from '../../~shared/services/unit-measure.service';
import { TaxProfileService } from '~core/services/tax-profile.service';
import { DashboardState } from './dashboard.state';
import { TaxTypeService } from '~core/services/tax-type.service';

export const DEFAULT_RESOURCE_STATE: ResourceStateModel = {
  disposition: 'list',
  resources: [],
  selectedResources: [],
  selectedResource: null,
  paginationData: { itemPerPage: 10 },
  filters: {},
  currentError: undefined,
  infiniteLoadingEnabled: undefined,
  paginationMode: 'page',
  cursorHistory: []
};

@State({
  name: RESOURCE_STATE_TOKEN,
  defaults: DEFAULT_RESOURCE_STATE
})
@Injectable()
export class ResourceState {
  private _store = inject(Store);
  private _globalFacade = inject(GlobalFacade);
  private _unitMeasureService = inject(UnitMeasureService);
  private _productService = inject(ProductService);
  private _roleService = inject(RolesService);
  private _userService = inject(UserService);
  private _auditService = inject(AuditService);
  private _importTaskService = inject(ImportTaskService);
  private _establishmentService = inject(EstablishmentService);
  private _salesPointService = inject(SalesPointService);
  private _organizationService = inject(OrganizationService);
  private _stickersMovementService = inject(StickerMovementService);
  private _invoiceService = inject(InvoiceService);
  private _organizationUsersService = inject(OrganizationUsersService);
  private _referenceDataService = inject(ReferenceDataService);
  private _currenciesService = inject(CurrenciesService);
  private _taxService = inject(TaxService);
  private _taxRulesService = inject(TaxRulesService);
  private _dashboardService = inject(DashboardService);
  private _paymentService = inject(PaymentService);
  private _receiptService = inject(ReceiptService);
  private _taxProfileService = inject(TaxProfileService);
  private _taxTypeService = inject(TaxTypeService);

  @Action(ResetState, { cancelUncompleted: true })
  resetState(ctx: StateContext<ResourceStateModel>, action: ResetState): void {
    const { disposition, paginationData } = ctx.getState();
    ctx.setState({
      ...DEFAULT_RESOURCE_STATE,
      disposition,
      paginationData: { itemPerPage: paginationData?.itemPerPage || 10 },
      infiniteLoadingEnabled: action.data?.infiniteLoadingEnabled || false,
      ...action.data
    });
  }

  @Action(PatchItem)
  patchItem(ctx: StateContext<ResourceStateModel>, action: PatchItem): void {
    const { resources } = ctx.getState();
    if (resources.length === 0) {
      return;
    }
    const { item, newData, path } = action.data;
    const itemIndex = resources.findIndex(el => el.id === item.id);
    if (itemIndex === -1) return;

    let newState: ResourceStateModel;
    if (path) {
      newState = immutable
        .wrap(ctx.getState())
        .set(`resources.${itemIndex}.${path}`, newData)
        .value();
    } else {
      newState = immutable
        .wrap(ctx.getState())
        .update(`resources.${itemIndex}`, resource => ({
          ...resource,
          ...newData
        }))
        .value();
    }

    ctx.setState(newState);
  }

  @Action(RemoveItem)
  removeItem(ctx: StateContext<ResourceStateModel>, action: RemoveItem): void {
    const { resources } = ctx.getState();
    const { id } = action.data;

    const filteredResources = resources.filter(el => el.id !== id);

    ctx.setState({
      ...ctx.getState(),
      resources: filteredResources
    });
  }

  @Action(ToggleItemSelection)
  toggleItemSelection(
    ctx: StateContext<ResourceStateModel>,
    action: ToggleItemSelection
  ): void {
    const { selectedResources } = ctx.getState();
    const item = action.data;
    const itemIndex = selectedResources.findIndex(el => el.id === item.id);
    if (itemIndex !== -1) {
      const newState = immutable
        .wrap(ctx.getState())
        .del(`selectedResource.${itemIndex}`)
        .value();

      ctx.setState(newState);
      return;
    }
    const newState = immutable
      .wrap(ctx.getState())
      .push(`selectedResources`, item)
      .value();

    ctx.setState(newState);
  }

  @Action(ToggleItemsSelection)
  toggleItemsSelection(
    ctx: StateContext<ResourceStateModel>,
    action: ToggleItemsSelection
  ): void {
    const desiredSelectionMode = action.data;
    const { selectedResources, resources } = ctx.getState();

    if (desiredSelectionMode === 'none') {
      ctx.patchState({ selectedResources: [] });
      return;
    }

    if (
      desiredSelectionMode === 'all' ||
      selectedResources.length !== resources.length
    ) {
      ctx.patchState({ selectedResources: resources });
      return;
    }

    ctx.patchState({ selectedResources: [] });
  }

  @Action(ToggleDisposition)
  toggleDisposition(ctx: StateContext<ResourceStateModel>): void {
    const { disposition } = ctx.getState();
    ctx.patchState({ disposition: disposition === 'list' ? 'grid' : 'list' });
  }

  @Action(ApplyFilters)
  applyFilters(
    ctx: StateContext<ResourceStateModel>,
    action: ApplyFilters
  ): void {
    const { filters } = ctx.getState();
    const filtersToApply = action.data;
    const filtersToBeApplied = removeNilProperties({
      ...filters,
      ...filtersToApply
    });
    ctx.patchState({ filters: filtersToBeApplied });
    this._resetAndFetch(ctx);
  }

  @Action(ResetFilters)
  resetFilters(
    ctx: StateContext<ResourceStateModel>,
    action: ResetFilters
  ): void | Observable<unknown> {
    const filtersToApply = action.data;
    ctx.patchState({
      filters: filtersToApply ? filtersToApply : DEFAULT_RESOURCE_STATE.filters
    });
    this._resetAndFetch(ctx);
  }

  @Action(SetPagination)
  setPagination(
    ctx: StateContext<ResourceStateModel>,
    action: SetPagination
  ): void | Observable<unknown> {
    const { paginationData, paginationMode } = ctx.getState();
    const paginationToApply = action.data;
    const paginationToBeApplied = paginationToApply
      ? { ...paginationData, ...paginationToApply }
      : undefined;

    ctx.patchState({
      paginationData: paginationToBeApplied
    });

    if (paginationMode === 'cursor') {
      this._resetAndFetch(ctx);
      return;
    }
    ctx.dispatch(new GetData({ refresh: true }));
  }

  @Action(LoadMore)
  loadMore(ctx: StateContext<ResourceStateModel>): Observable<unknown> {
    return ctx.dispatch(new GetData({ intent: 'append' }));
  }

  @Action(NavigateCursor)
  navigateCursor(
    ctx: StateContext<ResourceStateModel>,
    action: NavigateCursor
  ): Observable<unknown> {
    return ctx.dispatch(
      new GetData({
        intent: action.data === 'next' ? 'navigate-next' : 'navigate-previous'
      })
    );
  }

  private _resetAndFetch(ctx: StateContext<ResourceStateModel>): void {
    const { paginationMode, paginationData } = ctx.getState();
    if (paginationMode === 'cursor') {
      ctx.patchState({
        paginationData: {
          itemPerPage: paginationData?.itemPerPage,
          nextCursor: undefined,
          hasMore: undefined
        },
        cursorHistory: []
      });
      ctx.dispatch(new GetData({ intent: 'reset' }));
      return;
    }
    ctx.dispatch(new SetPagination({ page: 1 }));
  }

  @Action(GetData, { cancelUncompleted: true })
  getData(
    ctx: StateContext<ResourceStateModel>,
    action: GetData
  ): void | Observable<unknown> {
    ctx.patchState({ currentError: undefined });
    const { refresh, intent } = action.data || {};
    const {
      filters,
      paginationData,
      infiniteLoadingEnabled,
      resources,
      paginationMode,
      cursorHistory
    } = ctx.getState();

    if (paginationMode === 'cursor') {
      let workingHistory = [...cursorHistory];
      let cursor: string | null | undefined;

      if (intent === 'navigate-previous') {
        workingHistory.pop();
        if (workingHistory.length === 0) {
          return;
        }
        cursor = workingHistory[workingHistory.length - 1];
      } else if (intent === 'navigate-next' || intent === 'append') {
        cursor = paginationData?.nextCursor;
        if (cursor == null) {
          return;
        }
      } else {
        cursor = null;
        workingHistory = [];
      }

      const itemPerPage = paginationData?.itemPerPage;

      return ctx.dispatch(new ToggleItemsSelection('none')).pipe(
        switchMap(() => {
          const currentResourceTimeRangeQuery = this._store.selectSnapshot(
            DashboardState.currentResourceTimeRangeQuery
          );
          const params = {
            ...currentResourceTimeRangeQuery,
            ...filters,
            ...(cursor ? { cursor } : {}),
            itemPerPage
          };
          return this._getListRequest(params).pipe(
            tap({
              next: ({ items, ...rest }) => {
                const shouldPush =
                  intent !== 'navigate-previous' && intent !== 'append';
                const nextHistory = shouldPush
                  ? [...workingHistory, cursor ?? null]
                  : workingHistory;
                ctx.patchState({
                  resources:
                    intent === 'append' ? resources.concat(items) : items,
                  paginationData: { ...paginationData, ...rest },
                  cursorHistory: nextHistory
                });
              },
              error: (error: HttpErrorResponse) => {
                ctx.patchState({ currentError: error });
              }
            })
          );
        })
      );
    }

    let page = paginationData?.page || 1;
    const pageNumber = paginationData?.pageNumber;
    const itemPerPage = paginationData?.itemPerPage;
    if (!refresh && paginationData?.page) {
      page = page + 1;
    }
    if (!refresh && pageNumber && page > pageNumber) {
      return;
    }

    return ctx.dispatch(new ToggleItemsSelection('none')).pipe(
      switchMap(() => {
        const currentResourceTimeRangeQuery = this._store.selectSnapshot(
          DashboardState.currentResourceTimeRangeQuery
        );
        const params = {
          ...currentResourceTimeRangeQuery,
          ...filters,
          page,
          itemPerPage
        };
        const request$ = this._getListRequest(params);
        return request$.pipe(
          tap({
            next: ({ items, ...rest }) => {
              ctx.patchState({
                resources: infiniteLoadingEnabled
                  ? resources.concat(items)
                  : items,
                paginationData: rest
              });
            },
            error: (error: HttpErrorResponse) => {
              ctx.patchState({ currentError: error });
            }
          })
        );
      })
    );
  }

  // selectors

  @Selector()
  static disposition(
    state: ResourceStateModel
  ): Pick<ResourceStateModel, 'disposition'>['disposition'] {
    return state.disposition;
  }

  @Selector()
  static resources(
    state: ResourceStateModel
  ): Pick<ResourceStateModel, 'resources'>['resources'] {
    return state.resources;
  }

  @Selector()
  static currentError(
    state: ResourceStateModel
  ): Pick<ResourceStateModel, 'currentError'>['currentError'] {
    return state.currentError;
  }

  @Selector()
  static selectedResources(
    state: ResourceStateModel
  ): Pick<ResourceStateModel, 'selectedResources'>['selectedResources'] {
    return state.selectedResources;
  }

  @Selector()
  static selectedResource(state: ResourceStateModel): Resource | null {
    return state.selectedResource;
  }

  @Selector()
  static filters(
    state: ResourceStateModel
  ): Pick<ResourceStateModel, 'filters'>['filters'] {
    return state.filters;
  }

  @Selector()
  static paginationData(
    state: ResourceStateModel
  ): Pick<ResourceStateModel, 'paginationData'>['paginationData'] {
    return state.paginationData;
  }

  @Selector()
  static paginationMode(
    state: ResourceStateModel
  ): ResourceStateModel['paginationMode'] {
    return state.paginationMode;
  }

  @Selector()
  static cursorHistory(
    state: ResourceStateModel
  ): ResourceStateModel['cursorHistory'] {
    return state.cursorHistory;
  }

  @Selector()
  static hasPrevPage(state: ResourceStateModel): boolean {
    return state.cursorHistory.length > 1;
  }

  @Selector()
  static areAllResourcesSelected(state: ResourceStateModel): boolean {
    return (
      state.selectedResources.length > 0 &&
      state.selectedResources.length === state.resources.length
    );
  }

  @Selector()
  static haveSomeResourcesSelected(state: ResourceStateModel): boolean {
    return state.selectedResources.length > 0;
  }

  @Selector()
  static isSelected(
    state: ResourceStateModel
  ): (item: Resource | null | undefined) => boolean {
    return (item: Resource | null | undefined) =>
      item
        ? some(state.selectedResources, resource => resource.id === item.id)
        : false;
  }

  private _getListRequest(
    params: FreeRequestParams
  ): Observable<Pagination<Resource>> {
    const requestParams = { ...params, details: '' };
    const pageName = this._globalFacade.routeData?.data?.pageName;
    const { taxId, taxProfileId, organizationId } =
      this._globalFacade.routeData?.params || {};
    const isCursorMode =
      this._store.selectSnapshot(ResourceState.paginationMode) === 'cursor';

    const serviceMap: Partial<
      Record<
        PageName,
        (params?: FreeRequestParams) => Observable<Pagination<Resource>>
      >
    > = {
      [PageName.unitMeasure]: p => this._unitMeasureService.list(p),
      [PageName.product]: p => this._productService.list(p),
      [PageName.saleFormat]: p => this._productService.list(p),
      [PageName.ability]: p =>
        this._roleService.list({ ...p, organizationId: 'isNull()' }),
      [PageName.users]: p => this._userService.list({ ...p, isAdmin: true }),
      [PageName.auditLogs]: p => this._auditService.list(p),
      [PageName.importTask]: p => this._importTaskService.list(p),
      [PageName.organizationList]: p => this._organizationService.list(p),
      [PageName.establishment]: p => this._establishmentService.list(p),
      [PageName.salesPoint]: p => this._salesPointService.list(p),
      [PageName.userProfil]: p =>
        p?.userId ? this._organizationService.listUser(p.userId, p) : EMPTY,
      [PageName.stickers]: p => this._stickersMovementService.list(p),
      [PageName.invoices]: p => {
        const invoiceParams = {
          ...p,
          ...(!!p &&
            !('customStatus' in p) && {
              status: 'neq(draft)' as InvoiceStatus
            }),
          ...(!!p &&
            p.type === InvoiceType.proformaInvoice &&
            !('customStatus' in p) && {
              customStatus: CustomInvoiceStatus.proforma
            })
        };
        return isCursorMode
          ? this._invoiceService.listV2(invoiceParams)
          : this._invoiceService.list(invoiceParams);
      },
      [PageName.organizationInvoices]: p => {
        const invoiceParams = {
          ...p,
          ...(organizationId && { organizationId }),
          ...(!!p &&
            !('status' in p) && {
              status: 'neq(draft)' as InvoiceStatus
            })
        };
        return isCursorMode
          ? this._invoiceService.listV2(invoiceParams)
          : this._invoiceService.list(invoiceParams);
      },
      [PageName.organizationUsers]: p =>
        this._organizationUsersService.users(organizationId, p),
      [PageName.billingEquipments]: p =>
        this._referenceDataService.list({
          ...p,
          type: ReferenceDataType.salesPointEquipment
        }),
      [PageName.currencies]: p => this._currenciesService.list(p),
      [PageName.taxList]: p => this._taxService.list(p),
      [PageName.taxRules]: p =>
        this._taxRulesService.list({ ...p, ...(taxId && { taxId }) }),
      [PageName.customerSupplier]: p => {
        if (!p?.organizationId) return EMPTY;
        const { organizationId, ...restParams } = p;
        return this._organizationService.listPartners(
          organizationId,
          restParams
        );
      },
      [PageName.payments]: p => this._paymentService.list(p),
      [PageName.receiptList]: p => this._receiptService.list(p),
      [PageName.organizationReceipts]: p => this._receiptService.list(p),
      [PageName.dashboardList]: p =>
        this._dashboardService.listSuperSetDashboard(p),
      [PageName.taxProfiles]: p => this._taxProfileService.list(p),
      [PageName.taxProfileOrganizations]: p =>
        taxProfileId
          ? this._taxProfileService.listOrganizations(taxProfileId, p)
          : of({
              items: [],
              page: 1,
              itemPerPage: 10,
              pageNumber: 1,
              fullyItems: 1
            }),
      [PageName.taxTypes]: p => this._taxTypeService.list(p)
    };

    const serviceFn = pageName
      ? serviceMap[pageName as keyof typeof serviceMap]
      : undefined;

    return (
      serviceFn?.(requestParams) ??
      of({
        items: [],
        page: 1,
        itemPerPage: 10,
        pageNumber: 1,
        fullyItems: 1
      })
    );
  }
}
